import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Header,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  InvestigationSuggestion,
  Stage,
  TrackStarsReportSummary,
} from "../../lusca-search-api/generated-src";
import { useInvestigationSuggestions } from "../../hooks/useInvestigations";
import { TrackStarsSuggestionCard } from "./TrackStarsSuggestionCard";
import { formatTimestamp } from "./investigationUtils";
import { suggestionKey, suggestionReportId } from "./trackstarsText";
import { TrackStarsReportUpload } from "./TrackStarsReportUpload";
import { SafeExternalLink } from "./SafeExternalLink";

/** The suggestion list's contract with its host: hand the pattern to the population builder. */
export interface SuggestionStart {
  suggestion: InvestigationSuggestion;
  reportId?: string;
  /** The report the pattern came from, so the builder can weigh its coverage against the report's age. */
  report?: TrackStarsReportSummary;
}

interface InvestigationSuggestionsProps {
  stage: Stage;
  onStart: (start: SuggestionStart) => void;
  /** Overview mode: only this many cards (unowned first), with a "See all" link. */
  limit?: number;
  onSeeAll?: () => void;
  headerVariant?: "h2" | "h3";
}

const PRIORITY_RANK: Record<string, number> = { P1: 0, P2: 1, P3: 2, P4: 3 };

/** Unowned first, then by priority, then by size: the order someone would pick work in. */
export const rankSuggestions = (
  items: InvestigationSuggestion[]
): InvestigationSuggestion[] =>
  [...items].sort((a, b) => {
    const owned = (s: InvestigationSuggestion) =>
      s.ownership.state === "owned" ? 1 : 0;
    return (
      owned(a) - owned(b) ||
      (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
      b.totalMatches - a.totalMatches
    );
  });

/** Pattern ids a deep link (`?pattern=PAT-1,PAT-2`) asks the full page to jump to. */
const focusFromUrl = (): string[] =>
  (new URLSearchParams(window.location.search).get("pattern") ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const isFocused = (s: InvestigationSuggestion, ids: string[]): boolean =>
  ids.includes(s.patternId) ||
  ((s as { mergedPatternIds?: string[] }).mergedPatternIds ?? []).some((id) =>
    ids.includes(id)
  );

/**
 * "Suggested by TrackStars": the patterns TrackStars root-caused in its latest DLQ analysis, each a
 * population to claim, as cards a person can scan. Unowned patterns come first — they are the DLQ
 * work nobody has picked up that already comes with a root cause; covered ones stay visible so
 * nobody duplicates them.
 */
export const InvestigationSuggestions: React.FC<
  InvestigationSuggestionsProps
> = ({ stage, onStart, limit, onSeeAll, headerVariant = "h2" }) => {
  const query = useInvestigationSuggestions(stage);
  const { data } = query;
  const ranked = useMemo(
    () => rankSuggestions(data?.suggestions ?? []),
    [data?.suggestions]
  );
  const [nobodyOnly, setNobodyOnly] = useState(false);
  // Deep link from a backlog row's TrackStars popover: outline that pattern's card and scroll to it
  // once the list has loaded. Full page only; the overview's short list ignores it.
  const [focusIds] = useState<string[]>(() => (limit ? [] : focusFromUrl()));
  const focusRef = useRef<HTMLDivElement>(null);
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !focusRef.current) return;
    scrolled.current = true;
    focusRef.current.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [ranked]);
  // The overview only shows suggestions when there are some; the full page always offers the
  // upload, which is how a stage gets its first report.
  if (limit && (query.error || (data && !data.available))) return null;
  if (!query.isLoading && ranked.length === 0 && limit) return null;
  const noReport = Boolean(data && !data.available);

  const report = data?.report;
  const unownedList = ranked.filter((s) => s.ownership.state === "unowned");
  const unowned = unownedList.length;
  const shown = limit
    ? ranked.slice(0, limit)
    : nobodyOnly
    ? unownedList
    : ranked;

  // The full report (findings, recommendations, window) lives on the TrackStars dashboard.
  const provenance = report && (
    <SafeExternalLink href={report.reportUrl} fontSize="body-s">
      report from{" "}
      {report.generatedAt ? formatTimestamp(report.generatedAt) : "—"}
    </SafeExternalLink>
  );

  return (
    <SpaceBetween size="s">
      <Header
        variant={headerVariant}
        counter={ranked.length ? `(${ranked.length})` : undefined}
        description={
          report ? (
            <span>
              {provenance}
              {" · "}
              {report.patternsFound} pattern
              {report.patternsFound === 1 ? "" : "s"} explain{" "}
              {report.categorizationImprovementPct}% of the uncategorized DLQ
              {unowned > 0 ? (
                <>
                  {" · "}
                  <Box
                    variant="span"
                    color="text-status-error"
                    fontWeight="bold"
                    fontSize="body-s"
                  >
                    {unowned} nobody is on
                  </Box>
                </>
              ) : null}
              {report.stale && (
                <>
                  {" "}
                  <StatusIndicator type="warning">
                    older than {report.staleAfterDays} days
                  </StatusIndicator>
                </>
              )}
            </span>
          ) : undefined
        }
        actions={
          limit ? (
            onSeeAll &&
            ranked.length > limit && (
              <Button variant="normal" onClick={onSeeAll}>
                See all {ranked.length}
              </Button>
            )
          ) : (
            <TrackStarsReportUpload
              stage={stage}
              currentReportId={report?.reportId}
            />
          )
        }
      >
        {/* On its own page the page title already says "Suggested by TrackStars". */}
        {limit ? "Suggested by TrackStars" : "Patterns"}
      </Header>
      {!limit && ranked.length > 0 && (
        <SegmentedControl
          label="Show"
          selectedId={nobodyOnly ? "nobody" : "all"}
          onChange={({ detail }) =>
            setNobodyOnly(detail.selectedId === "nobody")
          }
          options={[
            { id: "all", text: `All (${ranked.length})` },
            { id: "nobody", text: `Nobody on it (${unowned})` },
          ]}
        />
      )}
      {query.isLoading && (
        <StatusIndicator type="loading">
          Loading TrackStars suggestions…
        </StatusIndicator>
      )}
      {query.error && (
        <Box color="text-body-secondary">
          TrackStars suggestions could not be loaded.
        </Box>
      )}
      {noReport && (
        <Box color="text-body-secondary">
          No TrackStars report for {stage} yet. Upload one to see its patterns
          here.
        </Box>
      )}
      {!query.isLoading && !noReport && !query.error && ranked.length === 0 && (
        <Box color="text-body-secondary">
          The latest TrackStars report found no patterns.
        </Box>
      )}
      {shown.map((s) => {
        const focused = focusIds.length > 0 && isFocused(s, focusIds);
        const card = (
          <TrackStarsSuggestionCard
            stage={stage}
            suggestion={s}
            reportUrl={report?.reportUrl}
            onInvestigate={(suggestion) =>
              onStart({
                suggestion,
                reportId: suggestionReportId(suggestion, report?.reportId),
                report,
              })
            }
          />
        );
        if (!focused) return <div key={suggestionKey(s)}>{card}</div>;
        const first = shown.find((x) => isFocused(x, focusIds)) === s;
        return (
          <div
            key={suggestionKey(s)}
            ref={first ? focusRef : undefined}
            data-testid={`suggestion-focused-${s.patternId}`}
            style={{
              outline: "2px solid #0972d3",
              borderRadius: "16px",
              scrollMarginTop: "80px",
            }}
          >
            {card}
          </div>
        );
      })}
    </SpaceBetween>
  );
};

export default InvestigationSuggestions;
